import CryptoJS from 'crypto-js';
import { PSYCHOMETRIC_ITEM_KIND, QUESTION_TYPES } from '../common/constants';
import { PRIVACY_POLICY, TERMS_AND_CONDITION } from './constants';
export const Toast = ({ msg }) => <>{msg}</>;

export const decryptData = (encryptedText, ENCRYPTION_KEY_HEX) => {
    try {
        if (!encryptedText || typeof encryptedText !== 'string' || !encryptedText.includes(':')) {
            throw new Error('Invalid encrypted text format');
        }

        // Extract IV and encrypted data from Base64
        const [ivBase64, encryptedBase64] = encryptedText.split(':');

        if (!ivBase64 || !encryptedBase64) {
            throw new Error('Invalid encryption format: Missing IV or encrypted data');
        }

        // Convert IV from Base64 to WordArray
        const iv = CryptoJS.enc.Base64.parse(ivBase64);
        const key = CryptoJS.enc.Utf8.parse(ENCRYPTION_KEY_HEX);
        // Decrypt using Base64 encoding
        const decrypted = CryptoJS.AES.decrypt({ ciphertext: CryptoJS.enc.Base64.parse(encryptedBase64) }, key, {
            iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
        });

        const value = decrypted.toString(CryptoJS.enc.Utf8);

        if (!value) {
            throw new Error('Decryption failed: Empty result');
        }

        return value;
    } catch (error) {
        console.error('❌ Decryption Error:', error.message);
        return null;
    }
};

export const decryptDataWithJSON = (encryptedText, ENCRYPTION_KEY_HEX) => {
    try {
        if (!encryptedText || typeof encryptedText !== 'string' || !encryptedText.includes(':')) {
            throw new Error('Invalid encrypted text format');
        }

        // Extract IV and encrypted data from Base64
        const [ivBase64, encryptedBase64] = encryptedText.split(':');

        if (!ivBase64 || !encryptedBase64) {
            throw new Error('Invalid encryption format: Missing IV or encrypted data');
        }

        // Convert IV from Base64 to WordArray
        const iv = CryptoJS.enc.Base64.parse(ivBase64);
        const key = CryptoJS.enc.Utf8.parse(ENCRYPTION_KEY_HEX);
        // Decrypt using Base64 encoding
        const decrypted = CryptoJS.AES.decrypt({ ciphertext: CryptoJS.enc.Base64.parse(encryptedBase64) }, key, {
            iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
        });

        const value = decrypted.toString(CryptoJS.enc.Utf8);
        if (!value) {
            throw new Error('Decryption failed: Empty result');
        }

        return JSON.parse(value);
    } catch (error) {
        console.error('❌ Decryption Error:', error.message);
        return null;
    }
};

/**
 * `candidateCallStart` may return psychometric questions as an encrypted string (iv:ciphertext)
 * identical to `questionDetails`, or as a plain JSON array.
 */
export const decryptPsychometricQuestionsIfNeeded = (raw, encryptionKeyHex, logPrefix = '[psychometricQuestions]') => {
    if (Array.isArray(raw)) {
        console.log(`${logPrefix} plain array, length=${raw.length}`);
        return raw;
    }
    if (raw == null || raw === '') {
        console.warn(`${logPrefix} missing or empty`);
        return [];
    }
    if (typeof raw !== 'string') {
        console.warn(`${logPrefix} unexpected type=${typeof raw}`);
        return [];
    }
    if (!raw.includes(':')) {
        console.warn(`${logPrefix} string is not iv:ciphertext format (no colon)`);
        return [];
    }
    if (!encryptionKeyHex) {
        console.error(`${logPrefix} cannot decrypt — encryptionKey missing from navigation state`);
        return [];
    }
    const parsed = decryptDataWithJSON(raw, encryptionKeyHex);
    if (parsed == null) {
        console.error(`${logPrefix} decryptDataWithJSON returned null (wrong key or corrupt payload)`);
        return [];
    }
    if (!Array.isArray(parsed)) {
        console.error(`${logPrefix} decrypted JSON is not an array, type=${typeof parsed}`);
        return [];
    }
    console.log(`${logPrefix} decrypted OK, array length=${parsed.length}`);
    return parsed;
};

export const tenantId = localStorage.getItem('tenantId');
export const capitalizeAll = (s) => {
    if (typeof s !== 'string') return '';
    return s
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export const formatTime = (seconds) => {
    const n = Number(seconds);
    if (!Number.isFinite(n) || n < 0) {
        return '00:00';
    }
    const minutes = Math.floor(n / 60);
    const remainingSeconds = n % 60;
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(remainingSeconds).padStart(2, '0');
    return `${formattedMinutes}:${formattedSeconds}`;
};

export const generateQuestionSuffix = (question, time) => {
    const listA = [
        `${question}\nYou have ${time} seconds to begin your answer. You can record your response after the beep sound.`,
        `${question}\nYou have ${time} seconds to begin your answer. You can start after the beep sound.`,
        `${question}\nPlease answer after the beep sound.`,
        `${question}\nYou will have ${time} seconds to prepare before. Please answer after the beep sound.`,
    ];
    const randomIndexA = Math.floor(Math.random() * listA.length);
    return listA[randomIndexA];
};

export const removeQuotes = (input) => {
    if (typeof input === 'string') {
        // Remove leading and trailing quotes if they exist
        return input.replace(/^"(.*)"$/, '$1');
    }
    return input; // Return as-is if it's not a string
};

export const getLatencyText = (name) => {
    // The candidate's own name is what makes this read as an acknowledgement rather
    // than a system message, so keep it — but a form without a name must not turn
    // into "Okay undefined."
    const candidate = typeof name === 'string' ? name.trim() : '';
    const lists = candidate ? [`Okay ${candidate}.`, `Alright ${candidate}.`] : ['Okay.', 'Alright.'];
    // Select a random index from the lists array
    const randomIndex = Math.floor(Math.random() * lists.length);
    return lists[randomIndex];
};

export const noAnswer = (name) => {
    const value = `The candidate skipped the question.`;
    return value;
};

export const validateEmail = (email) => {
    const regex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+\.[A-Za-z]{2,}$/;
    return regex.test(email);
};

// Check if the device is mobile or tablet
export const isMobileOrTabletDevice = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return (
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|tablet/.test(userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    ); // iPadOS detection
};

// Check if the device is strictly mobile (not tablet or iPad)
export const isMobileDevice = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return (
        /android|webos|iphone|ipod|blackberry|iemobile|opera mini/.test(userAgent) &&
        !(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    ); // Exclude iPadOS
};

export const getBrowserInfo = () => {
    const userAgent = navigator.userAgent;
    const browserName = (() => {
        if (userAgent.indexOf('Chrome') !== -1) return 'Chrome';
        if (userAgent.indexOf('Firefox') !== -1) return 'Firefox';
        if (userAgent.indexOf('Safari') !== -1) return 'Safari';
        if (userAgent.indexOf('Edge') !== -1) return 'Edge';
        if (userAgent.indexOf('Opera') !== -1 || userAgent.indexOf('OPR') !== -1) return 'Opera';
        if (userAgent.indexOf('MSIE') !== -1 || userAgent.indexOf('Trident/') !== -1) return 'Internet Explorer';
        return 'Unknown';
    })();

    return {
        browserName,
        browserVersion: navigator.appVersion,
    };
};

export const getBrowserInfoMessage = () => {
    let message;
    let browserInfo = getBrowserInfo();
    message = `Browser: ${browserInfo?.browserName}, Browser Version: ${browserInfo?.browserName}, Device Type: ${
        isMobileDevice() ? 'Mobile' : isMobileOrTabletDevice() ? 'Tablet' : 'Desktop/Laptop'
    }`;
    return message;
};
export const prepareTimeOverLink =
    'https://storage.googleapis.com/incruiter-media/audio_file/audio-1701862643-82158.mp3';
export const answerTimeOverLink =
    'https://storage.googleapis.com/incruiter-media/audio_file/audio-1701862720-92073.mp3';
export const interviewTimeOverLink =
    'https://storage.googleapis.com/incruiter-media/audio_file/audio-1701862799-26131.mp3';

export const generateUrl = (urlMode) => {
    const envMode = process.env.REACT_APP_ENV;
    let frontendUrlPreFix, backendUrlPreFix, externalUrl, interviewUrl, socketUrlPrefix;
    let finalUrl;
    if (envMode === 'prod') {
        interviewUrl = 'meeting.';
        frontendUrlPreFix = '';
        socketUrlPrefix = '';
        backendUrlPreFix = 'api.';
        externalUrl = 'https://prod-in2.100ms.live/hmsapi/incruiter-prod.app.100ms.live/';
    } else if (envMode === 'test' || envMode === 'preprod') {
        interviewUrl = 'test.v1.interview.';
        frontendUrlPreFix = 'test.v1.';
        socketUrlPrefix = 'test-';
        backendUrlPreFix = 'test.api.';
        externalUrl = 'https://prod-in2.100ms.live/hmsapi/incruiter-preprod-bot-room.app.100ms.live/';
    } else if (envMode === 'dev2') {
        interviewUrl = 'dev.v2.interview.';
        frontendUrlPreFix = 'dev.v2.';
        socketUrlPrefix = 'dev2-';
        backendUrlPreFix = 'dev.v2.api.';
        externalUrl = 'https://prod-in2.100ms.live/hmsapi/incruiter-dev-meet.app.100ms.live/';
    } else {
        interviewUrl = 'dev.v1.interview.';
        frontendUrlPreFix = 'dev.v1.';
        socketUrlPrefix = 'dev-';
        backendUrlPreFix = 'dev.api.';
        externalUrl = 'https://prod-in2.100ms.live/hmsapi/incruiter-dev-meet.app.100ms.live/';
    }
    if (urlMode === 'token_generation') {
        finalUrl = externalUrl;
    } else if (urlMode === 'socket') {
        finalUrl = `https://${socketUrlPrefix}incbot-system.incruiter.com`;
    } else if (urlMode === 'proctoring') {
        finalUrl = `https://${socketUrlPrefix}proctoring.incruiter.com`;
    } else if (urlMode === 'interview_backend') {
        finalUrl = `https://${backendUrlPreFix}incruiter.com/incbot-video-system/incbot`;
    } else if (urlMode === 'interview_system') {
        finalUrl = `https://${backendUrlPreFix}incruiter.com/interview-system/api/v1`;
    } else if (urlMode === 'video_backend') {
        finalUrl = `https://${backendUrlPreFix}incruiter.com/video-feedback-system/api/v1/video-platform`;
    } else if (urlMode === 'meeting') {
        finalUrl = `https://${interviewUrl}incruiter.com`;
    } else if (urlMode === 'user_backend') {
        finalUrl = `https://${backendUrlPreFix}incruiter.com/user-auth-system/api/v1`;
    } else if (urlMode === PRIVACY_POLICY) {
        finalUrl = `https://${frontendUrlPreFix}incruiter.com/privacy-policy`;
    } else if (urlMode === TERMS_AND_CONDITION) {
        finalUrl = `https://${frontendUrlPreFix}incruiter.com/terms-and-condition`;
    } else if (urlMode === 'siemensPolicy') {
        finalUrl = `https://${frontendUrlPreFix}incruiter.com/siemens-privacy-notice`;
    } else if (urlMode === 'conversational-ai') {
        finalUrl = `wss://${socketUrlPrefix}ai-services.incruiter.com/ws`;
    }
    return finalUrl;
};

export const isValidDataURI = (dataURI) => {
    return dataURI && dataURI.length > 10 && !dataURI.includes('png');
};

export const parseMarkdown = (markdownContent) => {
    // Replace Markdown syntax with HTML equivalents
    const htmlContent = markdownContent
        // Headings
        .replace(/^# (.+)$/gm, '<h1>$1</h1>')
        .replace(/^## (.+)$/gm, '<h2>$1</h2>')
        .replace(/^### (.+)$/gm, '<h3>$1</h3>')
        .replace(/^#### (.+)$/gm, '<h4>$1</h4>')
        .replace(/^##### (.+)$/gm, '<h5>$1</h5>')
        .replace(/^###### (.+)$/gm, '<h6>$1</h6>')

        // Bold and Italic
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')

        // Lists
        .replace(/^\s*-\s(.+)/gm, '<li>$1</li>')
        .replace(/^\s*1\.\s(.+)/gm, '<li>$1</li>')
        .replace(/^\s*\d+\.\s(.+)/gm, '<li>$1</li>')
        .replace(/<\/li><li>/g, '</li><li><ul>') // Wrap list items in <ul> tags
        .replace(/<\/li><\/ul><li>/g, '</li><li>') // Remove extra <ul> tags

        // Links
        .replace(/\[(.+?)\]\((\S+?)\)/g, '<a href="$2">$1</a>')

        // Images
        .replace(/!\[(.*?)\]\((.*?)\)/g, '<img alt="$1" src="$2" />')

        // Blockquotes
        .replace(/^\>(.*)$/gm, '<blockquote>$1</blockquote>')

        // Code blocks
        .replace(/```(.+?)\n([\s\S]+?)\n```/g, '<pre><code class="$1">$2</code></pre>')
        .replace(/``(.+?)``/g, '<code>$1</code>');

    return htmlContent;
};

export const getAssetSrc = (assetName) =>
    `https://incruiter.blob.core.windows.net/frontend/frontend-interview/assets/${assetName}`;

export const practiceQuestion = (number) => {
    let questionSet = null;

    switch (parseInt(number)) {
        case 1:
            questionSet = {
                response: `Hello, I hope you’re doing well.
I'm glad to have you here today.
I appreciate you taking the time to interview with us.
I'm looking forward to learning more about you and your experiences.
So, let's get started.
Tell me about yourself?.
You have 10 seconds to begin your answer. You can record your response after the beep sound.`,
                audioUrl: 'https://storage.googleapis.com/incruiter-media/audio_file_azure/audio-1722939778-117535.mp3',
                timeToPrepare: 10,
                timeToAnswer: 60,
                questionType: 'general',
                question: 'Tell me about yourself?',
                questionAnswer: '',
            };
            break;
        case 2:
            questionSet = {
                response: `Thanks for your response.
Let's move on to the next topic of discussion.
Why are you interested in this position?
You have 10 seconds to begin your answer.
You can record your response after the beep sound.`,
                audioUrl: 'https://storage.googleapis.com/incruiter-media/audio_file_azure/audio-1723010317-117194.mp3',
                timeToPrepare: 10,
                timeToAnswer: 60,
                questionType: 'general',
                question: 'Why are you interested in this position?',
                questionAnswer: '',
            };
            break;
        case 3:
            questionSet = {
                response: `Thanks for your response.
Let's move on to the next topic of discussion.
Describe your current job responsibilities.
You have 10 seconds to begin your answer.
You can record your response after the beep sound.`,
                audioUrl: 'https://storage.googleapis.com/incruiter-media/audio_file_azure/audio-1723010355-39097.mp3',
                timeToPrepare: 10,
                timeToAnswer: 60,
                questionType: 'general',
                question: 'Describe your current job responsibilities.',
                questionAnswer: '',
            };
            break;
        case 4:
            questionSet = {
                response: `That's all from my side.
Thank you for your time and for sharing your experiences and insights with us.
Good luck! Your practice interview has been completed.
Please click on the SUBMIT button to end the practice interview.`,
                audioUrl: 'https://storage.googleapis.com/incruiter-media/audio_file_azure/audio-1723010418-98437.mp3',
                timeToPrepare: 10,
                timeToAnswer: 60,
                questionType: 'general',
                question: `That's all from my side.
Thank you for your time and for sharing your experiences and insights with us.
Good luck! Your practice interview has been completed.
Please click on the SUBMIT button to end the practice interview.`,
                questionAnswer: '',
            };
            break;
        default:
            questionSet = {
                response: `Hello, I hope you’re doing well.
I'm glad to have you here today.
I appreciate you taking the time to interview with us.
I'm looking forward to learning more about you and your experiences.
So, let's get started.
Tell me about yourself?.
You have 10 seconds to begin your answer. You can record your response after the beep sound.`,
                audioUrl: 'https://storage.googleapis.com/incruiter-media/audio_file_azure/audio-1722939778-117535.mp3',
                timeToPrepare: 10,
                timeToAnswer: 60,
                questionType: 'general',
                question: 'Tell me about yourself?',
                questionAnswer: '',
            };
            break;
    }

    return questionSet;
};

const DEFAULT_ERROR_MSG = 'Something went wrong. Please try again.';
const generateError = (msg) => ({ status: false, msg });

export const validateFile = async ({
    file,
    maxSizeMB = 5,
    allowedTypes = [],
    endpoint = '',
    emptyMsg = 'File is empty or undefined',
    sizeMsg = 'The file size exceeds the limit',
    typeMsg = 'Invalid file type',
    fallbackMsg = 'Invalid file. Please upload a valid file',
}) => {
    try {
        if (!file) return generateError(emptyMsg);
        if (file.size > maxSizeMB * 1024 * 1024) return generateError(sizeMsg);
        const allowedTypesArray = Array.isArray(allowedTypes)
            ? allowedTypes
            : allowedTypes.split(',').map((type) => type.trim());

        if (!allowedTypesArray.includes(file.type)) return generateError(typeMsg);

        const formData = new FormData();
        formData.append('file', file);

        const url = `${generateUrl('interview_system')}/${endpoint}`;
        const headers = {};
        const tenantId = localStorage.getItem('tenantId');
        if (tenantId && tenantId !== 'undefined' && tenantId !== 'null') {
            headers['x-tenant-id'] = tenantId;
        }

        const response = await fetch(url, {
            method: 'POST',
            body: formData,
            headers,
        });

        const res = await response.json();

        return {
            status: res?.status ?? false,
            msg: res?.message || fallbackMsg,
        };
    } catch {
        return generateError(DEFAULT_ERROR_MSG);
    }
};

export const FILE_TYPES = {
    PDF_WORD: [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
        'application/msword', // .doc
        'application/x-msword',
        'application/x-cfb',
        'application/vnd.ms-word.document.macroenabled.12',
    ].join(', '),
};

export const checkFileBlank = (file) =>
    validateFile({
        file,
        maxSizeMB: 5,
        allowedTypes: FILE_TYPES.PDF_WORD,
        endpoint: 'validateFile',
        emptyMsg: 'File is empty or undefined',
        sizeMsg: 'The file size exceeds the 5MB limit',
        typeMsg: 'Invalid file type. Only .pdf, .docx, .doc are allowed',
        fallbackMsg: 'Invalid file type. Please upload a valid file',
    });

export const attachToken = (url, token) => {
    if (!url || !token) return url;

    if (Array.isArray(url)) {
        return url.map((u) => attachToken(u, token));
    }

    if (!url.includes('blob.core.windows.net')) return url;
    if (url.includes(token)) return url;

    return `${url}${url.includes('?') ? '&' : '?'}${token}`;
};

/**
 * Ensures written test questions always appear last in the flat question list, after all
 * coding and aptitude/MCQ questions. The backend sometimes inverts these relative to admin
 * order; navigation in mainViewConnector / FullyViewConnector follows questionDetails index order only.
 */
export const normalizeCodingBeforeWrittenQuestionOrder = (questionDetails) => {
    if (!Array.isArray(questionDetails) || questionDetails.length < 2) {
        return questionDetails;
    }

    const isCoding = (q) => q?.questionType === QUESTION_TYPES.CODING;
    const isWritten = (q) => q?.questionType === QUESTION_TYPES.WRITTEN_TEST || q?.questionType === 'written';
    const isMCQ = (q) => QUESTION_TYPES.MCQ_LIST.includes(q?.questionType);
    const isSpecial = (q) => isCoding(q) || isWritten(q) || isMCQ(q);

    const hasWritten = questionDetails.some(isWritten);
    const hasBeforeWritten = questionDetails.some((q) => isCoding(q) || isMCQ(q));
    if (!hasWritten || !hasBeforeWritten) {
        return questionDetails;
    }

    // Preserve original relative order of coding + aptitude, then written at the end
    const nonWrittenSpecial = questionDetails.filter((q) => isCoding(q) || isMCQ(q));
    const writtenQs = questionDetails.filter(isWritten);
    const merged = [...nonWrittenSpecial, ...writtenQs];

    let mi = 0;
    return questionDetails.map((q) => {
        if (!isSpecial(q)) {
            return q;
        }
        const next = merged[mi];
        mi += 1;
        return next;
    });
};

const psychometricMongoId = (item) => {
    const id = item?._id;
    if (id && typeof id === 'object' && id.$oid) {
        return id.$oid;
    }
    if (typeof id === 'string') {
        return id;
    }
    return id ?? '';
};

/**
 * Flattens backend `psychometricQuestions` (Coding & Aptitude psychometric flow) into
 * questionDetails rows compatible with {@link handleQuestionChange} in CodingAndAptitude.jsx.
 * Expects `item.type` of `subjective` (position-style prep + answer) or `option` (MCQ-style UI).
 */
const inferPsychometricItemKindFromPayload = (item) => {
    const raw = String(item?.type ?? item?.Type ?? item?.itemType ?? item?.item_type ?? '')
        .trim()
        .toLowerCase();
    if (raw === PSYCHOMETRIC_ITEM_KIND.SUBJECTIVE) {
        return PSYCHOMETRIC_ITEM_KIND.SUBJECTIVE;
    }
    if (
        raw === PSYCHOMETRIC_ITEM_KIND.OPTION ||
        raw === 'psychometric_mcq' ||
        raw === 'mcq' ||
        raw === 'multiple_choice'
    ) {
        return PSYCHOMETRIC_ITEM_KIND.OPTION;
    }
    const o = item?.options;
    if (typeof o === 'object' && o && (o.a != null || o.b != null || o.c != null || o.d != null || o.A != null)) {
        return PSYCHOMETRIC_ITEM_KIND.OPTION;
    }
    const hasFlatMcqOptions = ['option_1', 'option_2', 'option_3', 'option_4'].some(
        (k) => item?.[k] != null && String(item[k]).trim() !== ''
    );
    if (hasFlatMcqOptions) {
        return PSYCHOMETRIC_ITEM_KIND.OPTION;
    }
    return PSYCHOMETRIC_ITEM_KIND.SUBJECTIVE;
};

/**
 * All subjective rows first, then all option rows (order preserved within each group).
 */
export const orderPsychometricQuestionsSubjectiveBeforeOption = (rows) => {
    if (!Array.isArray(rows) || rows.length < 2) {
        return rows;
    }
    const subjective = [];
    const option = [];
    for (const row of rows) {
        const k = row?.psychometricItemType ?? inferPsychometricItemKindFromPayload(row);
        if (k === PSYCHOMETRIC_ITEM_KIND.SUBJECTIVE) {
            subjective.push(row);
        } else {
            option.push(row);
        }
    }
    return [...subjective, ...option];
};

/**
 * One intro clip per psychometric row. When a greeting/intro URL exists, use only that —
 * do not chain greeting + question audio (that caused a second read after the intro screen).
 */
const buildPsychometricAudioPlaylist = (greetingAudio, questionAudio, fallbackUrls) => {
    const greeting = typeof greetingAudio === 'string' ? greetingAudio.trim() : '';
    const question = typeof questionAudio === 'string' ? questionAudio.trim() : '';
    if (greeting) {
        return [greeting];
    }
    if (question) {
        return [question];
    }
    if (Array.isArray(fallbackUrls) && fallbackUrls.length > 0) {
        const first = fallbackUrls.find((u) => typeof u === 'string' && u.trim());
        return first ? [first.trim()] : undefined;
    }
    return undefined;
};

export const normalizePsychometricQuestionsForCA = (psychometricQuestions = [], envelope = {}) => {
    if (!Array.isArray(psychometricQuestions) || psychometricQuestions.length === 0) {
        return [];
    }
    const defSubPrep = Number(envelope.psychometricSubjectivePrepare) || 5;
    const defSubAns = Number(envelope.psychometricSubjectiveAnswer) || 120;
    const defOptAns = Number(envelope.psychometricOptionAnswer) || 60;

    // A closing/farewell item has no `type` field (real psychometric items always carry
    // `type: "subjective"` or `type: "option"`), no question text, and an explicit
    // timeToAnswer of 0. These must be appended AFTER all subjective + option rows so
    // the farewell audio/submit screen only appears once every question is answered.
    const isClosingItem = (item) => !item?.type && !String(item?.question ?? '').trim() && item?.timeToAnswer === 0;

    const closingRaw = psychometricQuestions.filter(isClosingItem);
    const regularRaw = psychometricQuestions.filter((item) => !isClosingItem(item));

    const mapped = regularRaw.map((item) => {
        const kind = inferPsychometricItemKindFromPayload(item);
        const opts = item?.options || {};
        const idStr = psychometricMongoId(item);

        const base = {
            questionType: QUESTION_TYPES.PSYCHOMETRIC_ASSESSMENT,
            psychometricItemType: kind,
            type: kind,
            question: item?.question,
            difficultyLevel: item?.level || item?.difficultyLevel,
            questionId: idStr ? { _id: idStr } : undefined,
            skill: item?.skill ?? '',
            sub_skill: item?.sub_skill,
        };

        // For convo-style data the raw arrays contain multiple sequential clips and must be
        // preserved whole. Fall back to buildPsychometricAudioPlaylist (single-clip) only
        // when the raw array carries at most one item.
        const resolveAudioArray = (rawArr, greetingAudio, singleUrl) => {
            if (Array.isArray(rawArr) && rawArr.length > 1) {
                const valid = rawArr.filter((u) => typeof u === 'string' && u.trim());
                if (valid.length > 0) return valid.map((u) => u.trim());
            }
            return buildPsychometricAudioPlaylist(greetingAudio, singleUrl, rawArr);
        };

        if (kind === PSYCHOMETRIC_ITEM_KIND.SUBJECTIVE) {
            const audioUrls = resolveAudioArray(item?.audioUrls, item?.greetingAudio, item?.audioUrl);
            // resumeAudioUrls is the Phase-2 question playlist played after the resume greeting;
            // do NOT include resumeGreetingAudio here — it is preserved as a separate field.
            const resumeAudioUrls = resolveAudioArray(item?.resumeAudioUrls, undefined, item?.resumeAudioUrl);
            return {
                ...base,
                timeToPrepare: item?.timeToPrepare ?? defSubPrep,
                timeToAnswer: item?.timeToAnswer ?? defSubAns,
                questionText: item?.questionText ?? item?.greeting ?? '',
                audioUrl: audioUrls?.[0] ?? item?.greetingAudio ?? item?.audioUrl,
                audioUrls,
                resumeGreeting: item?.resumeGreeting ?? item?.resumeQuestionText ?? '',
                resumeGreetingAudio: item?.resumeGreetingAudio ?? '',
                resumeQuestionText: item?.resumeGreeting ?? item?.resumeQuestionText ?? '',
                resumeAudioUrl: resumeAudioUrls?.[0] ?? item?.resumeAudioUrl ?? '',
                resumeAudioUrls,
            };
        }

        const audioUrls = resolveAudioArray(item?.audioUrls, item?.greetingAudio, item?.audioUrl);
        const resumeAudioUrls = resolveAudioArray(item?.resumeAudioUrls, undefined, item?.resumeAudioUrl);
        return {
            ...base,
            timeToPrepare: item?.timeToPrepare ?? 0,
            timeToAnswer: item?.timeToAnswer ?? defOptAns,
            option_1: opts.a ?? opts.A,
            option_2: opts.b ?? opts.B,
            option_3: opts.c ?? opts.C,
            option_4: opts.d ?? opts.D,
            questionText: item?.greeting ?? item?.questionText ?? '',
            resumeGreeting: item?.resumeGreeting ?? item?.resumeQuestionText ?? '',
            resumeGreetingAudio: item?.resumeGreetingAudio ?? '',
            resumeQuestionText: item?.resumeGreeting ?? item?.resumeQuestionText ?? '',
            audioUrl: audioUrls?.[0] ?? item?.greetingAudio ?? item?.audioUrl,
            audioUrls,
            resumeAudioUrl: resumeAudioUrls?.[0] ?? item?.resumeAudioUrl ?? '',
            resumeAudioUrls,
        };
    });

    const orderedRegular = orderPsychometricQuestionsSubjectiveBeforeOption(mapped);

    // Closing items are appended last so they appear only after every subjective and
    // option question has been answered. questionType is intentionally left empty so
    // the component does not attempt to save them as a psychometric answer — it only
    // plays the farewell audio and waits for the candidate to click Submit / End Call.
    const normalizedClosing = closingRaw.map((item) => ({
        questionType: '',
        psychometricItemType: null,
        type: null,
        question: item?.question ?? '',
        questionText: item?.questionText ?? '',
        resumeQuestionText: item?.resumeQuestionText ?? item?.questionText ?? '',
        timeToPrepare: 0,
        timeToAnswer: 0,
        audioUrl: item?.audioUrl,
        audioUrls: item?.audioUrl ? [item.audioUrl] : undefined,
        resumeAudioUrl: item?.resumeAudioUrl ?? item?.audioUrl,
        resumeAudioUrls: item?.resumeAudioUrl ? [item.resumeAudioUrl] : item?.audioUrl ? [item.audioUrl] : undefined,
    }));

    return [...orderedRegular, ...normalizedClosing];
};

/**
 * Plain-text answer for written test submission (no HTML). Prefer writtenAnswerText from the
 * editor; if missing, strip tags from writtenAnswer for older or partial state.
 */
export const getWrittenTestPlainAnswer = (writtenTestForm) => {
    const text = writtenTestForm?.writtenAnswerText;
    if (typeof text === 'string' && text.trim()) {
        return text.trim();
    }
    const html = writtenTestForm?.writtenAnswer;
    if (!html || typeof html !== 'string') {
        return '';
    }
    try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return (doc.body.textContent || '').replace(/\u00a0/g, ' ').trim();
    } catch {
        return '';
    }
};

export const isFullscreenActive = () =>
    Boolean(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
    );

let fullscreenBaseline = null;

const readFullscreenGeometry = () => ({
    outerWidth: window.outerWidth,
    outerHeight: window.outerHeight,
    viewportHeight: window.innerHeight * (window.outerWidth / window.innerWidth),
});

// const waitForFullscreen = async (timeoutMs = 800) => {
//     const startedAt = Date.now();
//     // Legacy prefixed APIs return undefined instead of a promise, so poll briefly before giving up.
//     while (!isFullscreenActive() && Date.now() - startedAt < timeoutMs) {
//         await new Promise((resolve) => setTimeout(resolve, 100));
//     }
//     return isFullscreenActive();
// };
// [INC-1814] After macOS Window Tiling, `fullscreenElement` stays set while the window is tiled, so it alone
// reported a failed re-entry as success and the violation modal never came back. Success now also requires the
// window to cover the screen; the longer timeout covers the macOS fullscreen animation that resizes the window.
// const waitForFullscreen = async (timeoutMs = 2000) => {
//     const startedAt = Date.now();
//     const entered = () => isFullscreenActive() && isWindowCoveringScreen();
    // Legacy prefixed APIs return undefined instead of a promise, so poll briefly before giving up.
//     while (!entered() && Date.now() - startedAt < timeoutMs) {
//         await new Promise((resolve) => setTimeout(resolve, 100));
//     }
//     return entered();
// };
const waitForFullscreen = async (timeoutMs = 2000) => {
    const startedAt = Date.now();
    let lastSize = '';
    let stableReads = 0;
    while (Date.now() - startedAt < timeoutMs) {
        if (isFullscreenActive()) {
            const size = `${window.outerWidth}x${window.outerHeight}x${window.innerWidth}x${window.innerHeight}`;
            stableReads = size === lastSize ? stableReads + 1 : 0;
            lastSize = size;
            if (isWindowCoveringScreen() || stableReads >= 3) break;
        }
        await new Promise((resolve) => setTimeout(resolve, 100));
    }
    if (!isFullscreenActive()) return false;
    fullscreenBaseline = readFullscreenGeometry();
    return true;
};

/**
 * Requests fullscreen and reports whether the browser actually entered it. The standard API returns
 * a promise that rejects when the call is not backed by a user gesture (transient activation), and
 * that rejection is silent, so the result must be checked instead of assumed.
 */
export const requestFullscreenSafely = async (element = document.documentElement) => {
    // if (isFullscreenActive()) return true;
    if (isFullscreenActive()) {
        if (isWindowCoveringScreen()) return true;
        // [INC-1814] Fullscreen flag is stale (window was tiled out of fullscreen on macOS). A fresh request on
        // an element that is already fullscreen is a no-op, so drop the stale state first.
        await exitFullscreenSafely();
        if (isFullscreenActive()) return false;
    }
    const request =
        element?.requestFullscreen ||
        element?.mozRequestFullScreen ||
        element?.webkitRequestFullscreen ||
        element?.msRequestFullscreen;
    if (!request) return false;
    try {
        await request.call(element);
    } catch (error) {
        return false;
    }
    return waitForFullscreen();
};

/**
 * Runs `handler` once, on the next user interaction. Returns a cleanup function.
 */
export const runOnNextUserGesture = (handler) => {
    const events = ['pointerdown', 'keydown', 'touchstart'];
    const cleanup = () => events.forEach((eventName) => document.removeEventListener(eventName, onGesture, true));
    // const onGesture = () => {
    const onGesture = (event) => {
        cleanup();
        // handler();
        handler(event);
    };
    events.forEach((eventName) => document.addEventListener(eventName, onGesture, true));
    return cleanup;
};

const browserFullscreenQuery = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(display-mode: fullscreen)')
        : null;

export const isBrowserFullscreen = () => !isMobileOrTabletDevice() && Boolean(browserFullscreenQuery()?.matches);

/**
 * [INC-1814] macOS Window Tiling (green button → Move & Resize / Fill & Arrange) pulls a fullscreen window out of
 * its fullscreen Space without Chrome/Edge firing `fullscreenchange` or clearing `document.fullscreenElement`, and
 * `(display-mode: fullscreen)` can keep matching. The only reliable signal left is the window's size, so fullscreen
 * also requires the browser window to cover the screen it is on.
 *
 * Width is checked tightly (every tiling layout, including Fill with margins, narrows the window). Height is checked
 * loosely so a notched MacBook — whose fullscreen area stops below the notch — is not flagged, while top/bottom
 * halves and Fill with a visible Dock still are.
 *
 * The page's viewport (inner size) is measured, not the window's outer size: the macOS green button puts Chrome in
 * browser full screen with the tab strip and toolbar still showing, where the window covers the screen but the page
 * does not. Height may fall short only by the MacBook notch strip (~38px), never by a toolbar (~75px+).
 */
// const FULLSCREEN_WIDTH_TOLERANCE_PX = 4;
// const FULLSCREEN_MIN_HEIGHT_RATIO = 0.9;
const FULLSCREEN_WIDTH_TOLERANCE_PX = 4;
const FULLSCREEN_HEIGHT_TOLERANCE_PX = 48;
// [INC-1814 follow-up] Browser toolbar height that still counts as fullscreen (toolbar is ~75px+, notch ~38px).
// const FULLSCREEN_TOOLBAR_TOLERANCE_PX = 48;
const FULLSCREEN_TOOLBAR_TOLERANCE_PX = 68;

// [INC-1814 follow-up] Window Tiling is macOS-only. Running the size check elsewhere ended live interviews on
// Windows/Linux setups where the page viewport never matches `screen` (browser zoom, multi-monitor, scaling).
const isMacDesktop = () =>
    typeof navigator !== 'undefined' &&
    /mac/i.test(navigator.userAgentData?.platform || navigator.platform || '') &&
    !isMobileOrTabletDevice();

// [INC-1814 Firefox fix] Firefox reports screen, window and page sizes in zoomed CSS pixels, each rounded on its own.
// At 80% and below or 150% and above they stop lining up, so the size check flagged real fullscreen and raised the
// popup. Firefox is checked on its fullscreen flag alone, as before INC-1814; other browsers are unchanged.
const isFirefox = () => typeof navigator !== 'undefined' && /firefox\//i.test(navigator.userAgent || '');

const isBrave = () => typeof navigator !== 'undefined' && Boolean(navigator.brave);

const matchesFullscreenBaseline = () => {
    if (!fullscreenBaseline || !isFullscreenActive()) return false;
    const now = readFullscreenGeometry();
    fullscreenBaseline = {
        outerWidth: Math.max(fullscreenBaseline.outerWidth, now.outerWidth),
        outerHeight: Math.max(fullscreenBaseline.outerHeight, now.outerHeight),
        viewportHeight: Math.max(fullscreenBaseline.viewportHeight, now.viewportHeight),
    };
    return (
        now.outerWidth >= fullscreenBaseline.outerWidth - FULLSCREEN_WIDTH_TOLERANCE_PX &&
        now.outerHeight >= fullscreenBaseline.outerHeight - FULLSCREEN_HEIGHT_TOLERANCE_PX &&
        now.viewportHeight >= fullscreenBaseline.viewportHeight - FULLSCREEN_TOOLBAR_TOLERANCE_PX
    );
};

export const isWindowCoveringScreen = () => {
    // if (typeof window === 'undefined' || !window.screen || isMobileOrTabletDevice()) return true;
    // if (typeof window === 'undefined' || !window.screen || !isMacDesktop()) return true;
    // if (typeof window === 'undefined' || !window.screen || !isMacDesktop() || isFirefox()) return true;
    if (typeof window === 'undefined' || !window.screen || !isMacDesktop() || isFirefox() || isBrave()) return true;
    const { width: screenWidth, height: screenHeight } = window.screen;
    // const { innerWidth, innerHeight } = window;
    const { innerWidth, innerHeight, outerWidth, outerHeight } = window;
    // Some embedded/headless contexts report 0 — never raise a violation on missing data.
    // if (!screenWidth || !screenHeight || !innerWidth || !innerHeight) return true;
    if (!screenWidth || !screenHeight || !innerWidth || !innerHeight || !outerWidth || !outerHeight) return true;
    // return (
    //     innerWidth >= screenWidth - FULLSCREEN_WIDTH_TOLERANCE_PX &&
    //     innerHeight >= screenHeight - FULLSCREEN_HEIGHT_TOLERANCE_PX
    // );
    // [INC-1814 follow-up] Inner sizes shrink with page zoom while `screen` and outer sizes do not, so comparing
    // inner to screen flagged every zoomed-in candidate as "not fullscreen" forever. Compare the window (outer)
    // to the screen instead, and detect a visible toolbar from outer vs inner height with zoom factored out
    // (in fullscreen nothing sits beside the page, so outerWidth / innerWidth is the zoom).
    const zoom = outerWidth / innerWidth;
    const toolbarHeight = outerHeight - innerHeight * zoom;
    // return (
    //     outerWidth >= screenWidth - FULLSCREEN_WIDTH_TOLERANCE_PX &&
    //     outerHeight >= screenHeight - FULLSCREEN_HEIGHT_TOLERANCE_PX &&
    //     toolbarHeight <= FULLSCREEN_TOOLBAR_TOLERANCE_PX
    // );
    const coversScreen =
        outerWidth >= screenWidth - FULLSCREEN_WIDTH_TOLERANCE_PX &&
        outerHeight >= screenHeight - FULLSCREEN_HEIGHT_TOLERANCE_PX &&
        toolbarHeight <= FULLSCREEN_TOOLBAR_TOLERANCE_PX;
    return matchesFullscreenBaseline() || coversScreen;
};

/**
 * [INC-1814 Esc fix] Browser-level fullscreen (no fullscreen element) is accepted only off macOS, where it means F11.
 * On macOS it means the window sits in a fullscreen Space (green button / Ctrl+Cmd+F): pressing Esc there leaves only
 * the page's fullscreen, the window stays in the Space, `(display-mode: fullscreen)` keeps matching and Chrome's
 * toolbar overlays the page instead of shrinking it — so it passed every check and the popup never opened, while the
 * candidate could reveal the tab strip and switch tabs. "Re-enter Fullscreen" restores element fullscreen there.
 */
const isAcceptedBrowserFullscreen = () => isBrowserFullscreen() && !isMacDesktop();

/** True only when the page is in fullscreen (element or browser-level) AND the window actually fills the screen. */
// export const isInFullscreen = () => (isFullscreenActive() || isBrowserFullscreen()) && isWindowCoveringScreen();
export const isInFullscreen = () => (isFullscreenActive() || isAcceptedBrowserFullscreen()) && isWindowCoveringScreen();

export const exitFullscreenSafely = async () => {
    const exit =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.mozCancelFullScreen ||
        document.msExitFullscreen;
    if (!exit || !isFullscreenActive()) return;
    try {
        await exit.call(document);
    } catch (error) {
        // Ignore — the caller re-checks state.
    }
    // [INC-1814] Wait for the flag to clear; requesting fullscreen while it is still set is a no-op.
    const startedAt = Date.now();
    while (isFullscreenActive() && Date.now() - startedAt < 1000) {
        await new Promise((resolve) => setTimeout(resolve, 50));
    }
};

const FULLSCREEN_SIZE_SETTLE_MS = 1500;
const FULLSCREEN_POLL_MS = 1000;

// [INC-1814 debug] Temporary diagnostics for the fullscreen popup not opening after Esc. Remove once resolved.
export const logFullscreenState = (label) => {
    try {
        console.log(`[Fullscreen] ${label}`, {
            inFullscreen: isInFullscreen(),
            fullscreenElement: Boolean(isFullscreenActive()),
            displayModeFullscreen: Boolean(browserFullscreenQuery()?.matches),
            coveringScreen: isWindowCoveringScreen(),
            macDesktop: isMacDesktop(),
            inner: `${window.innerWidth}x${window.innerHeight}`,
            outer: `${window.outerWidth}x${window.outerHeight}`,
            screen: `${window.screen?.width}x${window.screen?.height}`,
            time: new Date().toISOString(),
        });
    } catch (error) {
        // Diagnostics must never break the interview.
    }
};

export const watchFullscreenState = (onChange) => {
    const query = browserFullscreenQuery();
    // const isActive = () => isFullscreenActive() || isBrowserFullscreen();
    const isActive = isInFullscreen;
    let last = isActive();
    let settleTimer = null;
    logFullscreenState(`watcher started, initial state: ${last ? 'IN' : 'OUT OF'} fullscreen`);
    // const check = () => {
    //     const now = isActive();
    //     if (now === last) return;
    //     last = now;
    //     onChange();
    // };
    // [INC-1814 follow-up] Mid-resize (fullscreen enter animation, window drags) the window is briefly smaller than
    // the screen. When only the size check fails — the fullscreen flag is still set — wait for the window to settle
    // before treating it as an exit, so a transient size never logs a violation or starts the countdown.
    const commit = () => {
        const now = isActive();
        if (now === last) return;
        last = now;
        logFullscreenState(now ? 'ENTERED fullscreen' : 'EXITED fullscreen → raising popup');
        onChange();
    };
    const cancelSettle = () => {
        if (settleTimer) clearTimeout(settleTimer);
        settleTimer = null;
    };
    // const check = () => {
    //     if (settleTimer) clearTimeout(settleTimer);
    //     settleTimer = null;
    //     const sizeOnlyFailure = !isActive() && (isFullscreenActive() || isBrowserFullscreen());
    //     if (sizeOnlyFailure && last) {
    //         settleTimer = setTimeout(() => {
    //             settleTimer = null;
    //             commit();
    //         }, FULLSCREEN_SIZE_SETTLE_MS);
    //         return;
    //     }
    //     commit();
    // };
    // [INC-1814 Esc fix] Pressing Esc repeatedly exits fullscreen while macOS animates the window and
    // `(display-mode: fullscreen)` lags behind, so readings flicker. The old check cancelled the pending exit on
    // every event; one stale "still fullscreen" reading dropped the exit and, with no further event, the popup
    // never opened. Now a pending settle is only cancelled when the state really returns to what it was, the
    // settle clock is not restarted by every event, and a poll guarantees the final state is always seen.
    const check = (event) => {
        const now = isActive();
        if (now === last) {
            if (settleTimer) logFullscreenState(`pending change cancelled (${event?.type || 'poll'})`);
            cancelSettle();
            return;
        }
        logFullscreenState(`change detected via ${event?.type || 'poll'}`);
        // Exit where the fullscreen flag is still set (window resizing / tiled), or re-entry seen only through the
        // lagging display-mode query: wait for the window to settle. `commit` re-reads the state when it fires.
        // const sizeOnlyFailure = !now && (isFullscreenActive() || isBrowserFullscreen());
        const sizeOnlyFailure = !now && (isFullscreenActive() || isAcceptedBrowserFullscreen());
        const browserOnlyReentry = now && !isFullscreenActive();
        if (sizeOnlyFailure || browserOnlyReentry) {
            if (!settleTimer) {
                console.log(`[Fullscreen] waiting ${FULLSCREEN_SIZE_SETTLE_MS}ms for the window to settle`);
                settleTimer = setTimeout(() => {
                    settleTimer = null;
                    commit();
                }, FULLSCREEN_SIZE_SETTLE_MS);
            }
            return;
        }
        cancelSettle();
        commit();
    };
    document.addEventListener('fullscreenchange', check);
    document.addEventListener('webkitfullscreenchange', check);
    query?.addEventListener?.('change', check);
    // [INC-1814] Window Tiling only changes the window size — no fullscreen event fires.
    window.addEventListener('resize', check);
    // [INC-1814 Esc fix] Safety net for events lost or reordered during the exit animation.
    // const pollTimer = setInterval(check, FULLSCREEN_POLL_MS);
    const pollTimer = setInterval(() => check(), FULLSCREEN_POLL_MS);
    // [INC-1814 debug] The Esc that exits fullscreen is consumed by the browser; later presses reach the page.
    const logEscape = (event) => {
        if (event.key === 'Escape') logFullscreenState('Esc pressed');
    };
    document.addEventListener('keydown', logEscape, true);
    return () => {
        logFullscreenState('watcher stopped');
        document.removeEventListener('fullscreenchange', check);
        document.removeEventListener('webkitfullscreenchange', check);
        query?.removeEventListener?.('change', check);
        window.removeEventListener('resize', check);
        document.removeEventListener('keydown', logEscape, true);
        clearInterval(pollTimer);
        cancelSettle();
    };
};

export const retryFullscreenOnEveryGesture = ({ skip, onEnabled } = {}) => {
    let stop = null;
    let cancelled = false;
    const arm = () => {
        if (cancelled) return;
        stop = runOnNextUserGesture(async (event) => {
            // if (skip?.(event) || !(await requestFullscreenSafely())) arm();
            if (skip?.(event) || !(await requestFullscreenSafely())) {
                arm();
                return;
            }
            // onEnabled?.();
            if (!cancelled) onEnabled?.();
        });
    };
    arm();
    // return () => stop?.();
    return () => {
        cancelled = true;
        stop?.();
    };
};

/**
 * Enters fullscreen and recovers when the browser rejects the request. A rejected request never
 * fires `fullscreenchange`, so without recovery the interview keeps running in a windowed browser
 * and no violation is ever raised. Recovery retries on the candidate's next interaction (which
 * carries the user gesture the browser wants) and, if fullscreen is still not active after
 * `graceMs`, hands over to `onFailed` so the caller can run its usual violation flow.
 *
 * Returns a cleanup function that cancels any pending recovery.
 */
export const enterFullscreenWithRecovery = async ({ onEnabled, onFailed, graceMs = 5000, enforce = true } = {}) => {
    let graceTimer = null;
    let stopGestureRetry = null;

    const cleanup = () => {
        if (graceTimer) clearTimeout(graceTimer);
        if (stopGestureRetry) stopGestureRetry();
        graceTimer = null;
        stopGestureRetry = null;
    };

    if (await requestFullscreenSafely()) {
        onEnabled?.();
        return cleanup;
    }

    if (!enforce) return cleanup;

    stopGestureRetry = runOnNextUserGesture(async () => {
        stopGestureRetry = null;
        if (await requestFullscreenSafely()) {
            cleanup();
            onEnabled?.();
        }
    });

    graceTimer = setTimeout(() => {
        graceTimer = null;
        // if (!isFullscreenActive()) onFailed?.();
        // [INC-1814] A stale fullscreen flag from a tiled window must not cancel the violation flow.
        if (!isInFullscreen()) onFailed?.();
    }, graceMs);

    return cleanup;
};
